import { useEffect, useRef, useState } from 'react';
import { BroadcastChannel } from 'broadcast-channel';
import type { ViewingContextPayload } from './types';

/**
 * Channel name for broadcasting viewer context across windows.
 * The AI Sandbox subscribes to this channel to receive viewer state
 * since it runs in a separate browser window with isolated Recoil state.
 */
export const VIEWER_CONTEXT_CHANNEL = 'viewer-context-broadcast';

/**
 * Message types for the viewer context broadcast channel.
 */
export type ViewerContextBroadcastMessage =
  | {
      type: 'VIEWER_CONTEXT_UPDATE';
      payload: ViewingContextPayload;
      timestamp: number;
    }
  | {
      type: 'VIEWER_CONTEXT_CLEAR';
      timestamp: number;
    }
  | {
      type: 'VIEWER_CONTEXT_REQUEST';
      timestamp: number;
    };

// Singleton channel instance for broadcasting
let broadcasterChannel: BroadcastChannel<ViewerContextBroadcastMessage> | null = null;

// Store the last broadcasted context so we can respond to requests
let lastBroadcastedContext: ViewingContextPayload | null = null;

/**
 * Reset the broadcaster state. Only for use in tests.
 * @internal
 */
export function __resetBroadcasterForTesting(): void {
  broadcasterChannel?.close();
  broadcasterChannel = null;
  lastBroadcastedContext = null;
}

function getBroadcasterChannel(): BroadcastChannel<ViewerContextBroadcastMessage> {
  if (broadcasterChannel == null) {
    broadcasterChannel = new BroadcastChannel<ViewerContextBroadcastMessage>(
      VIEWER_CONTEXT_CHANNEL
    );

    // Set up listener for context requests from subscribers
    broadcasterChannel.onmessage = (message: ViewerContextBroadcastMessage) => {
      if (message.type === 'VIEWER_CONTEXT_REQUEST' && lastBroadcastedContext != null) {
        // Respond to request by re-broadcasting the current context
        broadcasterChannel?.postMessage({
          type: 'VIEWER_CONTEXT_UPDATE',
          payload: lastBroadcastedContext,
          timestamp: Date.now(),
        });
      }
    };
  }
  return broadcasterChannel;
}

/**
 * Broadcasts the current viewer context to all listening windows (e.g., AI Sandbox).
 * This should be called from the viewer window whenever the context changes.
 */
export function broadcastViewerContext(context: ViewingContextPayload | null): void {
  const channel = getBroadcasterChannel();

  // Store the context so we can respond to late subscribers
  lastBroadcastedContext = context;

  if (context == null) {
    channel.postMessage({
      type: 'VIEWER_CONTEXT_CLEAR',
      timestamp: Date.now(),
    });
  } else {
    channel.postMessage({
      type: 'VIEWER_CONTEXT_UPDATE',
      payload: context,
      timestamp: Date.now(),
    });
  }
}

/**
 * Hook to subscribe to viewer context broadcasts from other windows.
 * Used by AI Sandbox to receive viewer state since it runs in a separate window.
 *
 * @returns The latest viewer context received, or null if no context available
 */
export function useViewerContextSubscription(): ViewingContextPayload | null {
  const [context, setContext] = useState<ViewingContextPayload | null>(null);
  const channelRef = useRef<BroadcastChannel<ViewerContextBroadcastMessage> | null>(null);

  useEffect(() => {
    // Create a new channel for subscribing (separate from broadcaster singleton)
    const channel = new BroadcastChannel<ViewerContextBroadcastMessage>(VIEWER_CONTEXT_CHANNEL);
    channelRef.current = channel;

    const handleMessage = (message: ViewerContextBroadcastMessage) => {
      if (message.type === 'VIEWER_CONTEXT_UPDATE') {
        setContext(message.payload);
      } else if (message.type === 'VIEWER_CONTEXT_CLEAR') {
        setContext(null);
      }
    };

    channel.onmessage = handleMessage;

    // Request current context from broadcaster (handles late subscriber case)
    // Small delay to ensure the message handler is set up first
    const requestTimeoutId = setTimeout(() => {
      channel.postMessage({
        type: 'VIEWER_CONTEXT_REQUEST',
        timestamp: Date.now(),
      });
    }, 0);

    return () => {
      // Cancel the pending request post first; otherwise it can fire after
      // channel.close() and throw "Cannot post message after channel has
      // closed" (an unhandled error when a consumer unmounts quickly). (VX-3970)
      clearTimeout(requestTimeoutId);
      channel.close();
      channelRef.current = null;
    };
  }, []);

  return context;
}
